import { useList, useListProps } from 'app/definitions';
import { useEffect, useMemo, useState } from 'react';
import useInfiniteScroll from '..';

type UseInfinitiveProps = useListProps & {
  resource: string;
};

export function useInfinitive(options: UseInfinitiveProps) {
  const { pagination: paginationFromProps } = options;
  const [pagination, setPagination] = useState(paginationFromProps);
  const [cached, setCached] = useState(new Map());
  const query = useList({
    ...options,
    pagination
  });
  const { isUninitialized, isLoading, data } = query;

  const allPageData = useMemo(() => {
    if (cached.size === 0) return [];
    return Array.from(cached.values()).reduce((acc, page) => {
      return [...acc, ...page];
    }, []);
  }, [cached]);

  const hasMore = useMemo(() => {
    if (data?.pagination) {
      return allPageData.length < data?.pagination?.total;
    }
    return false;
  }, [data?.pagination, allPageData?.length]);

  const { loaderRef } = useInfiniteScroll({
    loadMore: async () => {
      if (isUninitialized) return;
      setPagination((prev) => {
        return {
          ...prev,
          current: prev.current + 1
        };
      });
    },
    hasMore,
    isLoading: isUninitialized ? true : isLoading
  });

  useEffect(() => {
    if (data?.list) {
      setCached((prev) => {
        const newCached = new Map(prev);
        newCached.set(pagination.current, data.list);
        return newCached;
      });
    }
  }, [data?.list]);

  return [
    loaderRef,
    {
      ...query,
      data: allPageData,
      hasMore
    }
  ] as const;
}
